import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import { Layers, LayersOutlined } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { manageKeyboardEvents } from "shared/common/utils/keyboardEvents/manageKeyboardEvents";
import PopoverBox from "shared/common/utils/popover/PopoverBox";
import LayerContent from "./LayerContent";
import HoverImagePreview, {
  HoverImagePreviewHandle,
  relevantImageUrl,
} from "./HoverImagePreview";

interface Props {
  authMode: "view" | "edit";
}

const isNetworkLayer = (layer: mm.Layer): layer is mm.NetworkLayer =>
  layer.type === mm.LAYERTYPE.NETWORKLAYER && layer instanceof mm.NetworkLayer;

const findAxisByFeature = (feature: mm.Feature): mm.AxisObject | undefined => {
  const axisCollection = defaultStore.getState().axisCollection;
  if (!axisCollection) return undefined;

  return axisCollection.find((axis) =>
    axis.axis?.axis?.features.some(
      (axisFeature: mm.Feature) => axisFeature.id === feature.id
    )
  );
};

export default function Layer({ authMode }: Props) {
  const { t } = useTranslation();

  const hoverPreviewRef = useRef<HoverImagePreviewHandle>(null);

  const viewer = defaultStore((state) => state.viewer);
  const layerConfiguration = defaultStore((state) => state.layerConfiguration);

  const getUpdatedImageInfoPopover = sidebarStore(
    (state) => state.getUpdatedImageInfoPopover
  );
  const changeTopicPopover = sidebarStore((state) => state.changeTopicPopover);
  const topicPopover = sidebarStore((state) => state.topicPopover);
  const changeSelectedTableRows = sidebarStore(
    (state) => state.changeSelectedTableRows
  );
  const selectTools = sidebarStore((state) => state.selectTools);
  const changeSelectTools = sidebarStore((state) => state.changeSelectTools);
  const activeAddLayersListItem = sidebarStore(
    (state) => state.activeAddLayersListItem
  );
  const expandAddLayersList = sidebarStore(
    (state) => state.expandAddLayersList
  );
  const closeAllToolPopover = sidebarStore(
    (state) => state.closeAllToolPopover
  );
  const changeHoveredAxis = sidebarStore((state) => state.changeHoveredAxis);
  const changeSelectedAxis = sidebarStore((state) => state.changeSelectedAxis);
  const changeAxisControlPopover = sidebarStore(
    (state) => state.changeAxisControlPopover
  );
  const changeInfosPopover = sidebarStore((state) => state.changeInfosPopover);

  //change topic button and popover
  const handleClickTopic = () => {
    closeAllToolPopover();
    changeTopicPopover(!topicPopover);
  };

  //add select feature option object and functions
  const picklayers: mm.Layer[] = [];
  const getLayerByFeature = (feature: mm.Feature) =>
    viewer
      ?.getScene()
      .getLayerHandler()
      .getLayerByFeatureCollection(feature.parentcollection);

  const tooloptions = {
    disablecbf: () => {
      changeInfosPopover(false);
    },
    selectedcbf: (selected_feature: mm.Feature) => {
      if (!viewer) return;
      const layer = getLayerByFeature(selected_feature);
      if (!layer) return;

      layer.featureselector.clearSelection();

      closeAllToolPopover();

      if (isNetworkLayer(layer)) {
        const matchedAxis = findAxisByFeature(selected_feature);
        if (!matchedAxis) return;

        layer.addAxisToSelection(matchedAxis.axis);
        changeSelectedAxis(matchedAxis.axis);
        viewer
          .getScene()
          .getLRHandler()
          .setActiveMeasurementAxis(matchedAxis.axis);
        changeAxisControlPopover(true);

        viewer
          .getScene()
          .getLayerHandler()
          .getActiveLayer()
          ?.featureselector.clearSelection();
        viewer.getScene().getLayerHandler().setActiveLayer(null);
        changeSelectedTableRows(undefined);
      } else {
        layer.featureselector.addToSelection(selected_feature.id);

        viewer.getScene().getLayerHandler().setActiveLayer(layer);
        const geojson = selected_feature.toGeoJSON();
        changeSelectedTableRows(geojson);
        changeTopicPopover(!getUpdatedImageInfoPopover());
      }
    },
    deselectedcbf: (deselected_feature: mm.Feature) => {
      if (!viewer) return;

      const layer = getLayerByFeature(deselected_feature);
      if (!layer) return;

      if (isNetworkLayer(layer)) {
        const matchedAxis = findAxisByFeature(deselected_feature);
        if (!matchedAxis) return;

        layer.removeAxisFromSelection(matchedAxis.axis);
        changeSelectedAxis(null);
        viewer.getScene().getLRHandler().setActiveMeasurementAxis(null);
      } else {
        layer.featureselector.removeFromSelection(deselected_feature.id);
        viewer.getScene().getLayerHandler().setActiveLayer(null);
        changeSelectedTableRows(undefined);
      }
    },
    clearcbf: () => {
      picklayers.forEach((pickLayer) =>
        pickLayer.featureselector.clearSelection()
      );
    },
    onovercbf: (feature_hovered: mm.Feature) => {
      if (!viewer) return;

      hoverPreviewRef.current?.onFeatureHover(
        relevantImageUrl(feature_hovered)
      );

      const layer = getLayerByFeature(feature_hovered);
      if (!layer) return;

      if (isNetworkLayer(layer)) {
        const axis = findAxisByFeature(feature_hovered);
        if (!axis) return;

        layer.axisHighlightOn(axis.axis);
        changeHoveredAxis(axis.axis);
      } else {
        feature_hovered.highlightOn();
      }
    },
    onoutcbf: (feature_out: mm.Feature) => {
      if (!viewer) return;

      hoverPreviewRef.current?.onFeatureHover(null);

      const layer = getLayerByFeature(feature_out);
      if (!layer) return;

      if (isNetworkLayer(layer)) {
        changeHoveredAxis(null);
        const axis = findAxisByFeature(feature_out);
        if (!axis) return;

        if (axis.axis === sidebarStore.getState().selectedAxis) return; // do not remove selection if the axis is selected
        layer.axisHighlightOff(axis.axis);
      } else {
        if (sidebarStore.getState().selectedTableRows?.id === feature_out.id)
          return; // do not remove selection if the feature is selected
        feature_out.highlightOff();
      }
    },
    hightlightonhover: false,
    activecursor: "pointer",
    clearonnullpick: true,
    singleactive: true,
    selectionmodel: mm.SELECTIONMODEL.SIMPLE,
  };

  //create select tool option when viewer is defined
  useEffect(() => {
    if (viewer !== undefined) {
      changeSelectTools(
        viewer
          .getScene()
          .getToolHandler()
          .createSelectFeatureTool(tooloptions, "select")
      );
    }
  }, [viewer]);

  // Add all the layers to the picklayers
  useEffect(() => {
    let event_nr: number | undefined = undefined;
    if (selectTools !== undefined && viewer !== undefined) {
      viewer
        .getScene()
        .getLayerHandler()
        .layers.forEach((layer: mm.Layer) => {
          selectTools.addToPickLayer(layer.getFeatureCollection());
        });

      event_nr = viewer.getScene().userInputHandler.subscribeEvent(
        mm.USERINPUTTYPE["LAYERADDED"],
        function (layer: mm.Layer) {
          selectTools.addToPickLayer(layer.getFeatureCollection());
          if (layer.getLayerEditor()) {
            selectTools.addToPickLayer(layer.getLayerEditor().featuresinsert);
          }
        },
        undefined
      );
    }
    return () => {
      if (!event_nr || viewer === undefined) return;
      viewer
        .getScene()
        .userInputHandler.unSubscribeEvent(
          mm.USERINPUTTYPE["LAYERADDED"],
          event_nr
        );
    };
  }, [selectTools]);

  //manage keyboard events while writing on inputs
  useEffect(() => {
    if (expandAddLayersList && topicPopover && activeAddLayersListItem !== "") {
      manageKeyboardEvents(viewer, true);
    } else {
      manageKeyboardEvents(viewer, false);
    }
  }, [activeAddLayersListItem, expandAddLayersList, topicPopover]);

  return (
    <>
      <Tooltip
        title={t("tooltip.layer")}
        placement="right"
        enterDelay={400}
        classes={{
          tooltip: "infra3d-buttonDescription",
        }}
      >
        <IconButton
          id="infra3d-layer-button"
          sx={{
            width: "40px",
            mx: "auto",
            color: "white",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
          classes={{
            root: topicPopover
              ? "infra3d-iconButtonRippleActive"
              : "infra3d-iconButtonRipple",
          }}
          onClick={() =>
            monitor(apm, "change-status-layertree | Toolbar", handleClickTopic)
          }
        >
          {!topicPopover && <LayersOutlined />}
          {topicPopover && <Layers />}
        </IconButton>
      </Tooltip>
      <PopoverBox visible={topicPopover}>
        {viewer !== undefined && layerConfiguration !== undefined && (
          <LayerContent authMode={authMode} />
        )}
      </PopoverBox>
      <HoverImagePreview ref={hoverPreviewRef} />
    </>
  );
}
